export interface AnyObject {
  [key: string]: unknown
}

const objectNotEmpty = <T>(
  object: T | undefined | Record<string, never>
): object is T => Object.values(object || {}).length > 0

export interface DeleteFalsyAndUnwantedKeyProps<T> {
  object: T
  unwantedKeys?: string[]
  keepZeroes?: boolean
  keepFalseBooleans?: boolean
}

/** based on https://stackoverflow.com/a/61770643, _ is a temporary name */
const getObjectWithoutKey = <
  WantedType extends Record<string, unknown>,
  UnwantedKey extends string
>(
  object: WantedType & Partial<Record<UnwantedKey, unknown>>,
  unwantedKey: UnwantedKey
): WantedType => {
  const { [unwantedKey]: _, ...restOfObject } = object
  return restOfObject as WantedType
}

export type GetWithoutFalsyOrUnwantedKeys = <
  WantedType,
  UnwantedKeys extends string[]
>(args: {
  object: UnwantedKeys extends undefined
    ? WantedType & Partial<Record<string, undefined | false | 0 | null>>
    : WantedType & Partial<Record<UnwantedKeys[number], unknown>>
  unwantedKeys?: UnwantedKeys
  keepZeroes?: boolean
  keepFalseBooleans?: boolean
}) => WantedType
/** Goes through object and returns only true or wanted values
 * Pass unwantedKeys prop to take them out of the originalObject
 *
 * keepZeroes makes sure zeroes are not deleted, needed for form handling
 *
 * Creating a new object instead of deleting old one,
 * because deleting causes field to visually go empty
 */
const getWithoutFalsyOrUnwantedKeys: GetWithoutFalsyOrUnwantedKeys = ({
  object,
  unwantedKeys,
  keepZeroes,
  keepFalseBooleans
}) =>
  Object.entries(object).reduce((objectAcc, [currentKey, value]) => {
    const isAllowedKey = !unwantedKeys?.includes(currentKey)
    /** make sure to handle keepZeroes and allowFalseBoolean.
     * eg user input is "waterPrice": 0€, do not delete waterPrice
     */
    const isAllowedValue = !!(
      value ||
      (keepFalseBooleans && value === false) ||
      (keepZeroes && value === 0)
    )
    const keepKeyInObject = isAllowedKey && isAllowedValue
    if (keepKeyInObject) {
      return objectAcc
    } else {
      return getObjectWithoutKey(objectAcc, currentKey)
    }
  }, object)

const getObjectWithoutUnwantedKeys = <
  WantedType extends Record<string, unknown>,
  UnwantedKeys extends string[]
>(
  originalObject:
    | WantedType
    | (WantedType & Partial<Record<UnwantedKeys[number], unknown>>),
  unwantedKeys: UnwantedKeys
): WantedType =>
  unwantedKeys.reduce((objectAcc, unwantedKey) => {
    return getObjectWithoutKey(objectAcc, unwantedKey)
  }, originalObject)

export {
  objectNotEmpty,
  getWithoutFalsyOrUnwantedKeys,
  getObjectWithoutUnwantedKeys
}
